import classNames from 'classnames';

import React, {
  Children,
  createElement,
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  RefAttributes,
  useEffect,
  useState,
} from 'react';

import styles from './chips.scss';

type Props = RefAttributes<HTMLDivElement> &
  Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'children'> & {
    onChange?: (value: ReadonlyArray<string | number>) => void;
    horizontal?: boolean;
    children?: ReadonlyArray<ReactElement>;
    selectedValues?: ReadonlyArray<string | number>;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-chips';

export const Chips: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      children,
      onChange,
      horizontal = false,
      selectedValues = [],
      dataTestId = COMPONENT_ID,
      ...rest
    },
    ref,
  ) => {
    const [selectedChipsValue, setSelectedChipsValue] =
      useState<ReadonlyArray<string | number>>(selectedValues);

    const classes = classNames(styles.container, className, {
      [styles.horizontal]: horizontal,
    });

    const handleChange = (value: string, checked: boolean) => {
      setSelectedChipsValue((state) =>
        checked ? state.filter((val) => val !== value) : [...state, value],
      );
      onChange?.(selectedChipsValue);
    };

    const isChecked = (val: string) => {
      if (selectedValues.length > 0) {
        return selectedValues.some((value) => value === val);
      } else if (selectedChipsValue.length > 0) {
        return selectedChipsValue.some((value) => value === val);
      }
      return false;
    };

    useEffect(() => {
      if (Children.toArray(children).length > 0) {
        setSelectedChipsValue(
          children.filter(({ props }) => props.checked).flatMap(({ props }) => props.value),
        );
      }
    }, [children]);

    return (
      <div
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        className={classes}
        ref={ref}
        {...rest}>
        {Children.map(children, (child) =>
          createElement(child.type, {
            ...child.props,
            key: child.props.value,
            onChange: handleChange,
            checked: isChecked(child.props.value),
          }),
        )}
      </div>
    );
  },
);

Chips.displayName = 'Chips';
